/* screens/kitchen/editors/IngredientEditor.jsx — редактор карточки сырья (drawer type='card').
   S4: полная версия. Вкладки Нутриенты / Закупки / Использование / История.
   Нутриенты: название, категория, единица учёта (Граммы/Штуки → «Граммов в 1 штуке»),
   КБЖУ на 100 г (+ AI-заполнение со стрим-имитацией), 14 аллергенов (ТР ТС 022/2011).
   Закупки: PriceForm (фиксация цены + история) + заметки.
   Использование: блюда/полуфабрикаты (клик → push ttk). История: VersionsBlock (&v=n).
   Удаление: ConfirmDialog danger + защита card_in_use. ХЕНДОФФ → frontend/pages/kitchen/IngredientEditor.jsx. */

import React from 'react';
import { useChat } from '../../../app/providers/ChatProvider.jsx';
import { useConfirm } from '../../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { useUnsaved } from '../../../app/providers/UnsavedChangesProvider.jsx';
import { AllergenChips } from '../../../components/kitchen/AllergenControls.jsx';
import { PriceForm } from '../../../components/kitchen/PriceForm.jsx';
import { EntityEditor, EntityTitleInput } from '../../../components/patterns/EntityEditor.jsx';
import { EmptyState, QueryBoundary } from '../../../components/patterns/States.jsx';
import { KindBadge, StatusBadge } from '../../../components/ui/Badge.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { IconButton } from '../../../components/ui/Buttons.jsx';
import { Field, NativeSelect, NumberInput, Segmented, TextInput, Textarea } from '../../../components/ui/Field.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { VersionsBlock } from '../../../components/versioning/VersionsBlock.jsx';
import { hooks } from '../../../hooks/index.jsx';
import {
  getCardDetail, createCardDetail, updateCardDetail, deleteCardDetail,
  addCardPrice, deleteCardPrice, estimateKbju, setCardPieceWeight,
} from '../../../lib/api/index.js';
import { INGREDIENT_CATEGORIES, blankCardDetail as blankIngredientDetail } from '../../../lib/adapters/ingredients.js';
import { aiClosedDetail } from '../../../lib/aiAccess.js';
import { fmt } from '../../../lib/format.js';
import { RR } from '../../../lib/router.jsx';

  const { useState, useEffect, useMemo } = React;
  const { useFakeQuery, useDrawerStack, useBreakpoint } = hooks;
  const { useSearchParams } = RR;

  const KBJU_FIELDS = [
    { key: 'kcal_per_100g', label: 'Ккал', unit: 'ккал' },
    { key: 'protein_per_100g', label: 'Белки', unit: 'г' },
    { key: 'fat_per_100g', label: 'Жиры', unit: 'г' },
    { key: 'carbs_per_100g', label: 'Углеводы', unit: 'г' },
  ];

  function IngredientEditor({ id, onClose }) {
    const { push, stack } = useDrawerStack();
    const { isMobile } = useBreakpoint();
    const toast = useToast();
    const confirm = useConfirm();
    const chat = useChat ? useChat() : null;
    const unsaved = useUnsaved ? useUnsaved() : null;
    const clearGuard = () => unsaved && unsaved.register && unsaved.register('card-' + id, false);
    const isNew = id === 'new' || String(id).startsWith('new-');

    const query = useFakeQuery('card-' + id, () => (isNew ? Promise.resolve(null) : getCardDetail(id)), [id]);

    const [params] = useSearchParams();
    // &v скоупится к ВЕРХНЕМУ сегменту ?edit (IA §4.1) — читаем v только если этот редактор верхний.
    const isTop = !!stack.length && stack[stack.length - 1].type === 'card' && String(stack[stack.length - 1].id) === String(id);
    const wantHistory = !isNew && ((isTop && !!params.get('v')) || params.get('etab') === 'history');
    const [card, setCard] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState(wantHistory ? 'history' : 'nutrients');
    const [nameErr, setNameErr] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);        // стрим-заполнение КБЖУ
    const [aiFilled, setAiFilled] = useState({});       // {key:true} — поля, заполненные AI (derived-стиль)

    useEffect(() => {
      if (isNew) setCard(blankIngredientDetail());
      else if (query.data) setCard(JSON.parse(JSON.stringify(query.data)));
    }, [query.data, id]);

    // hook ДО ранних return (правило хуков); guard на null card
    const currentSnapshot = useMemo(() => (card ? {
      name: card.name, category: card.category, default_unit: card.default_unit,
      kcal_per_100g: card.kcal_per_100g, protein_per_100g: card.protein_per_100g,
      fat_per_100g: card.fat_per_100g, carbs_per_100g: card.carbs_per_100g,
      allergens: card.allergens, notes: card.notes, current_price: card.current_price,
    } : null), [card]);

    if (!isNew && (query.loading || (!card && !query.error))) {
      return <div className="ttk-editor"><div className="ttk-skel">{[0, 1, 2].map((i) => <div key={i} className="skeleton ttk-skel__block" />)}</div></div>;
    }
    if (!isNew && query.error) return <div className="ttk-editor"><QueryBoundary error={query.error} onRetry={query.reload}>{null}</QueryBoundary></div>;
    if (!card) return null;

    const patch = (p) => { setCard((c) => ({ ...c, ...p })); setDirty(true); };

    const setUnit = (u) => patch(u === 'шт' ? { default_unit: 'шт' } : { default_unit: 'г', piece_weight_g: null });

    // правка КБЖУ-поля вручную снимает derived-метку «заполнено AI»
    const patchKbju = (key, v) => { patch({ [key]: v }); setAiFilled((s) => { if (!s[key]) return s; const n = { ...s }; delete n[key]; return n; }); };

    // AI-заполнение КБЖУ на 100 г: боевой LLM-вызов по имени (+категории) —
    // POST /api/kitchen/cards/estimate-kbju/. Кнопка под спиннером во время запроса,
    // по ответу все 4 поля заполняются разом (derived-стиль). Значения — оценка, повар правит.
    const aiFillKbju = async () => {
      const name = (card.name || '').trim();
      if (!name) { toast.show('Сначала укажите название сырья', 'warning'); setTab('nutrients'); return; }
      setAiBusy(true); setAiFilled({});
      toast.show('Ассистент оценивает пищевую ценность…', 'accent', 2000);
      try {
        const k = await estimateKbju(name, card.category);
        const num = (v) => (v == null ? null : Number(v));
        setCard((c) => ({
          ...c,
          kcal_per_100g: num(k.kcal), protein_per_100g: num(k.protein),
          fat_per_100g: num(k.fat), carbs_per_100g: num(k.carbs),
        }));
        setAiFilled({ kcal_per_100g: true, protein_per_100g: true, fat_per_100g: true, carbs_per_100g: true });
        setDirty(true);
        toast.show('КБЖУ заполнено. Проверьте и поправьте при необходимости.', 'success');
      } catch (e) {
        // 402 subscription_expired: оценка — запрос к модели, а доступ к ней у
        // пространства без тарифа закрыт; говорим это текстом бэкенда.
        const closed = aiClosedDetail(e);
        if (closed) toast.show(closed, 'warning', 8000);
        else toast.show('Не удалось оценить КБЖУ', 'danger');
      }
      setAiBusy(false);
    };

    const briefOf = (p) => (p ? { amount_rub: p.amount_rub, unit: p.unit, effective_from: p.effective_from, supplier_name: p.supplier_name } : null);
    // Цены существующей карточки — отдельные эндпоинты (PATCH их игнорит). Сохраняем сразу и
    // обновляем ТОЛЬКО prices/current_price из свежего detail (не затирая несохранённые правки нутриентов).
    const refreshPrices = async () => {
      try {
        const fresh = await getCardDetail(id);
        setCard((c) => (c ? { ...c, prices: fresh.prices, current_price: fresh.current_price } : c));
      } catch { /* noop */ }
    };
    const addPrice = async (price) => {
      // Цена «₽/шт» на карточку без веса штуки: форма прислала piece_weight_g —
      // без него ₽/шт не конвертируется в ₽/г и себестоимость блюд не считается.
      const pieceW = price.piece_weight_g;
      if (isNew) {
        setCard((c) => {
          const prices = [price, ...(c.prices || [])].sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1));
          const withPiece = pieceW ? { default_unit: 'шт', piece_weight_g: pieceW } : {};
          return { ...c, ...withPiece, prices, current_price: briefOf(prices[0]) };
        });
        setDirty(true);
        return;
      }
      try {
        // Вес — точечным PATCH'ем ДО цены: сигнал бэка после цены сразу
        // пересчитает себестоимость по готовой ставке ₽/г.
        if (pieceW) {
          await setCardPieceWeight(id, pieceW);
          setCard((c) => (c ? { ...c, default_unit: 'шт', piece_weight_g: pieceW } : c));
        }
        await addCardPrice(id, price);
        await refreshPrices();
        window.dispatchEvent(new CustomEvent('kitchen:cards-changed'));
        toast.show('Цена обновлена', 'success');
      }
      catch (e) { toast.show(e.detail || 'Не удалось добавить цену', 'danger'); }
    };
    const delPrice = async (pid) => {
      if (isNew) { setCard((c) => { const prices = (c.prices || []).filter((p) => p.id !== pid); return { ...c, prices, current_price: briefOf(prices[0]) }; }); setDirty(true); return; }
      try { await deleteCardPrice(pid); await refreshPrices(); window.dispatchEvent(new CustomEvent('kitchen:cards-changed')); toast.show('Цена удалена', 'muted'); }
      catch (e) { toast.show('Не удалось удалить цену', 'danger'); }
    };

    const save = async () => {
      if (!card.name.trim()) { setNameErr('Укажите название'); setTab('nutrients'); return; }
      setBusy(true); setNameErr(null);
      try {
        if (isNew) await createCardDetail(card);
        else await updateCardDetail(id, card);
        window.dispatchEvent(new CustomEvent('kitchen:cards-changed'));
        toast.show(isNew ? 'Карточка создана' : 'Сохранено. Версия зафиксирована.', 'success');
        setDirty(false); clearGuard(); onClose && onClose();
      } catch (e) {
        if (e.code === 'duplicate_name') { setNameErr('Сырьё с таким названием уже есть'); setTab('nutrients'); }
        else toast.show('Не удалось сохранить', 'danger');
        setBusy(false);
      }
    };

    const del = async () => {
      const ok = await confirm({ title: 'Удалить карточку сырья?', body: 'Карточка «' + card.name + '» будет удалена. Это действие необратимо.', confirmLabel: 'Удалить', tone: 'danger' });
      if (!ok) return;
      setBusy(true);
      try {
        await deleteCardDetail(id);
        window.dispatchEvent(new CustomEvent('kitchen:cards-changed'));
        toast.show('Карточка удалена', 'success');
        setDirty(false); clearGuard(); onClose && onClose();
      } catch (e) {
        const used = e.usages_count ?? e.count;
        if (e.code === 'card_in_use') toast.show('Нельзя удалить: используется в ' + used + ' ' + fmt.plural(used, 'ТТК', 'ТТК', 'ТТК'), 'danger', 4000);
        else toast.show('Не удалось удалить', 'danger');
        setBusy(false);
      }
    };

    const usages = card.usages || [];
    const dishes = usages.filter((u) => u.ttk_kind_label !== 'Полуфабрикат');
    const semis = usages.filter((u) => u.ttk_kind_label === 'Полуфабрикат');

    const tabs = [
      { value: 'nutrients', label: 'Нутриенты' },
      { value: 'purchasing', label: 'Закупки' },
      { value: 'usage', label: 'Использование', disabled: isNew },
      { value: 'history', label: 'История', disabled: isNew },
    ].filter((t) => !t.disabled);

    const footer = (
      <>
        {!isNew && <Button variant="ghost" iconLeft="trash" onClick={del} disabled={busy} className="ttk-foot__del">Удалить</Button>}
        <div className="grow" />
        <Button variant="ghost" onClick={() => onClose && onClose()} disabled={busy}>Отмена</Button>
        <Button variant="primary" onClick={save} loading={busy} disabled={!dirty && !isNew}>{isNew ? 'Создать' : 'Сохранить'}</Button>
      </>
    );

    // «Редактировать в чате»: новый чат с привязкой этой карточки (metadata.edit_target)
    // — модель правит её через propose_card_edit, изменения приходят канвасом на одобрение.
    const editInChat = () => {
      if (!chat || !chat.spawnEditChat) { toast.show('Чат недоступен', 'muted'); return; }
      chat.spawnEditChat({ kind: 'card', id, title: card.name });
    };

    return (
      <EntityEditor
        dirtyKey={'card-' + id} dirty={dirty}
        titleField={(
          <Field error={nameErr} className="ttk-title-field">
            <EntityTitleInput value={card.name} placeholder="Название сырья" invalid={nameErr} ariaLabel="Название сырья"
              onChange={(e) => { patch({ name: e.target.value }); if (nameErr) setNameErr(null); }} />
          </Field>
        )}
        kindBadge={<KindBadge kind="ingredient" size="sm" />}
        headActions={!isNew && (
          <IconButton icon="sparkles" variant="ghost" size="sm" tooltip="Редактировать в чате" onClick={editInChat} />
        )}
        tabs={tabs} activeTab={tab} onTab={setTab}
        footer={footer}>

        {/* ── Нутриенты ── */}
        {tab === 'nutrients' && (
          <div className="card-editor">
            <div className="card-editor__row2">
              <Field label="Категория" className="card-editor__type">
                <NativeSelect value={card.category} options={INGREDIENT_CATEGORIES || []} onChange={(e) => patch({ category: e.target.value })} />
              </Field>
              <Field label="Единица учёта">
                <Segmented value={card.default_unit} onChange={setUnit}
                  options={[{ value: 'г', label: 'Граммы' }, { value: 'шт', label: 'Штуки' }]} />
              </Field>
              {card.default_unit === 'шт' && (
                <Field label="Граммов в 1 штуке" hint="Для пересчёта в граммы">
                  <NumberInput value={card.piece_weight_g ?? ''} unit="г" step={1} min={0} onChange={(v) => patch({ piece_weight_g: v === '' ? null : Number(v) })} />
                </Field>
              )}
            </div>

            <section className="kbju-edit">
              <div className="kbju-edit__head">
                <h4 className="ttk-sub-title t-caption">Пищевая ценность на 100 г</h4>
                <Button variant="secondary" size="sm" iconLeft="sparkles" loading={aiBusy} onClick={aiFillKbju}>Заполнить через AI</Button>
              </div>
              <div className="kbju-edit__grid">
                {KBJU_FIELDS.map((f) => (
                  <Field key={f.key} label={aiFilled[f.key]
                    ? <span className="kbju-edit__label-ai">{f.label}<span className="derived-tag">AI</span></span>
                    : f.label}>
                    <NumberInput plain className={aiFilled[f.key] ? 'is-ai-filled' : undefined}
                      value={card[f.key] ?? ''} unit={f.unit} step={f.key === 'kcal_per_100g' ? 5 : 0.1} min={0}
                      onChange={(v) => patchKbju(f.key, v === '' ? null : Number(v))} />
                  </Field>
                ))}
              </div>
              {Object.keys(aiFilled).length > 0 && (
                <p className="kbju-edit__note t-caption text-mute">Значения оценены ассистентом — проверьте и поправьте при необходимости.</p>
              )}
            </section>

            <section className="card-allergens">
              <h4 className="ttk-sub-title t-caption">Аллергены</h4>
              <AllergenChips value={card.allergens || []} onChange={(v) => patch({ allergens: v })} />
            </section>
          </div>
        )}

        {/* ── Закупки ── */}
        {tab === 'purchasing' && (
          <div className="card-editor">
            {card.current_price && (
              <div className="cur-price derived-zone">
                <div className="cur-price__main">
                  <span className="cur-price__amount num">{fmt.money(card.current_price.amount_rub)}/{card.current_price.unit}</span>
                  <span className="t-caption text-dim">Текущая закупочная цена</span>
                </div>
                <div className="cur-price__meta t-caption text-dim">
                  {card.current_price.supplier_name && <span>{card.current_price.supplier_name}</span>}
                  <span>с {fmt.date(card.current_price.effective_from)}</span>
                </div>
              </div>
            )}
            <PriceForm prices={card.prices || []} onAdd={addPrice} onDelete={delPrice}
              pieceWeightG={card.piece_weight_g} />
            <Field label="Заметки">
              <Textarea value={card.notes || ''} minRows={2} placeholder="Поставщик, особенности хранения, замены…" onChange={(e) => patch({ notes: e.target.value })} />
            </Field>
          </div>
        )}

        {/* ── Использование ── */}
        {tab === 'usage' && (
          <div className="card-usage">
            {usages.length === 0 ? (
              <EmptyState serif={false} icon="link" title="Не используется" body="Это сырьё пока не входит ни в одну ТТК или полуфабрикат." />
            ) : (
              <>
                {dishes.length > 0 && <UsageList title={'Блюда (' + dishes.length + ')'} items={dishes} onOpen={(tid) => push('ttk', tid)} />}
                {semis.length > 0 && <UsageList title={'Полуфабрикаты (' + semis.length + ')'} items={semis} onOpen={(tid) => push('ttk', tid)} />}
              </>
            )}
          </div>
        )}

        {/* ── История ── */}
        {tab === 'history' && (
          <VersionsBlock kind="card" id={id} currentSnapshot={currentSnapshot} />
        )}
      </EntityEditor>
    );
  }

  function UsageList({ title, items, onOpen }) {
    return (
      <section className="used-in__sec">
        <h4 className="used-in__title t-caption">{title}</h4>
        <ul className="used-in__list">
          {items.map((u) => (
            <li key={u.ttk_id}>
              <button type="button" className="used-in__link" onClick={() => onOpen(u.ttk_id)}>
                <Icon name="file-text" size={15} /><span className="grow truncate">{u.ttk_title}</span>
                <span className="num t-caption text-dim">{fmt.grams(u.amount_netto_g)}</span>
                <Icon name="chevron-right" size={15} className="text-mute" />
              </button>
            </li>
          ))}
        </ul>
      </section>
    );
  }

export { IngredientEditor };
